import React, { useEffect, useRef, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
    Alert,
    Checkbox,
    FormControlLabel,
    FormControl,
    FormLabel,
    Slider,
    Tooltip,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import axios from 'axios';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import { Category } from '../../types/Category';
import {
    suggestTreeFromCategory,
    suggestParentStructureOverCategories,
    fetchCategoryWithChildren,
    SUGGEST_TREE_REQUEST_TIMEOUT_MS,
} from '../../services/CategoryService';
import { historyService } from '../../services/HistoryService';
import api from '../../services/api';
import { useHelpContent } from '../../utils/HelpUtils';
import { normalizePresentation } from '../../utils/CategoryUtils';

function findCategoryInTree(categories: Category[], targetId: string): Category | null {
    const category = categories.find((cat) => cat.id === targetId);
    if (category) return category;
    for (const parent of categories) {
        if (parent.children) {
            const found = findCategoryInTree(parent.children, targetId);
            if (found) return found;
        }
    }
    return null;
}

/** Collapse nested category nodes so each top-level bucket has only content leaves (matches server organize apply). */
function collectSuggestedContentLeaves(node: any, out: any[]): void {
    for (const c of node.children || []) {
        if (c?.type === 'content') {
            out.push(c);
        } else if (c?.type === 'category') {
            collectSuggestedContentLeaves(c, out);
        }
    }
}

function flattenSuggestedCategoryChildrenToContentOnly(root: any): void {
    if (!root?.children) {
        return;
    }
    for (const child of root.children) {
        if (child?.type !== 'category') {
            continue;
        }
        const flat: any[] = [];
        collectSuggestedContentLeaves(child, flat);
        child.children = flat;
    }
}

type SuggestTreeOriginalContent = { id?: string; text: string; subcase?: string };

/** Matches Java {@code CategoryService.clusteringInputText} — keys align with embedding-service leaves. */
function clusteringInputText(text: string | null | undefined): string {
    if (text == null) return ' ';
    const n = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    if (n.trim() === '') return ' ';
    return n;
}

/** Map an AI content leaf to original row text + subcase for preview (leaves usually have text only, no index). */
function resolveOriginalContentForLeaf(
    leaf: { index?: number; text?: string; id?: string },
    originalContents: SuggestTreeOriginalContent[] | undefined,
    usedIndices: Set<number>,
): { text: string; subcase: string } | null {
    if (!originalContents?.length) {
        return leaf.text ? { text: leaf.text, subcase: '' } : null;
    }

    if (leaf.index !== undefined && leaf.index >= 0 && leaf.index < originalContents.length) {
        const o = originalContents[leaf.index];
        usedIndices.add(leaf.index);
        return { text: o.text ?? '', subcase: o.subcase ?? '' };
    }

    if (leaf.id) {
        const idx = originalContents.findIndex((o, i) => o.id === leaf.id && !usedIndices.has(i));
        if (idx >= 0) {
            usedIndices.add(idx);
            const o = originalContents[idx];
            return { text: o.text ?? '', subcase: o.subcase ?? '' };
        }
    }

    if (leaf.text) {
        const key = clusteringInputText(leaf.text);
        for (let i = 0; i < originalContents.length; i++) {
            if (usedIndices.has(i)) continue;
            if (clusteringInputText(originalContents[i].text) === key) {
                usedIndices.add(i);
                const o = originalContents[i];
                return { text: o.text ?? '', subcase: o.subcase ?? '' };
            }
        }
        const cleanText = leaf.text.replace(/^SUBCASE:\s*/i, '').trim();
        for (let i = 0; i < originalContents.length; i++) {
            if (usedIndices.has(i)) continue;
            const ot = originalContents[i].text ?? '';
            if (ot.includes(cleanText) || cleanText.includes(ot)) {
                usedIndices.add(i);
                const o = originalContents[i];
                return { text: o.text ?? '', subcase: o.subcase ?? '' };
            }
        }
        return { text: leaf.text, subcase: '' };
    }

    return null;
}

export type OrganizeCategoriesMode = 'fromContent' | 'branch';

export type OrganizeCategoriesPreset = 'initialCategories' | null;

type BranchOrganizeOperation = 'split_children' | 'group_siblings';

type ChildSubtreeSuggestion = {
    categoryId: string;
    categoryName: string;
    suggested: { tree: any; originalContents?: SuggestTreeOriginalContent[] };
};

/** Stable key for a top-level proposed parent group in branch bottom-up preview. */
function parentGroupKey(node: any, index: number): string {
    return `${index}:${String(node?.name ?? '').trim()}`;
}

interface GenerateSubtreeDialogProps {
    open: boolean;
    onClose: () => void;
    mode: OrganizeCategoriesMode;
    /** When set on open, applies bottom-up strategy and depth 1 (initial categories workflow). */
    preset?: OrganizeCategoriesPreset;
    category: Category | null;
    anchorCategory?: Category | null;
    siblingCategoryIds?: string[];
    /** Direct children that are content categories with at least two rows (top-down on branch). */
    splittableChildCategories?: Category[];
    /** True while splittableChildCategories is still being resolved (branch mode). */
    splittableChildrenLoading?: boolean;
    categoriesTree?: Category[];
    onSuccess: (updatedCategoryId?: string, operationData?: any) => void;
    setGlobalLoading?: (loading: boolean) => void;
}

const SOURCE_CAT_MARKER = /\n__SRC_CAT__=([^\n]+)$/;

const MAX_DEPTH_STORAGE_KEY = 'structureCategoriesMaxDepth';

/** Last depth the user picked in this dialog; defaults to 1 for first-time use. */
function loadRememberedMaxDepth(): number {
    try {
        const raw = window.localStorage.getItem(MAX_DEPTH_STORAGE_KEY);
        const n = raw == null ? NaN : parseInt(raw, 10);
        if (Number.isInteger(n) && n >= 1 && n <= 5) return n;
    } catch {
        // localStorage unavailable (private mode etc.) — fall through to default
    }
    return 1;
}

function saveRememberedMaxDepth(depth: number): void {
    try {
        window.localStorage.setItem(MAX_DEPTH_STORAGE_KEY, String(depth));
    } catch {
        // best-effort persistence only
    }
}

const GenerateSubtreeDialog: React.FC<GenerateSubtreeDialogProps> = (props) => {
    const {
        open,
        onClose,
        mode,
        preset = null,
        category,
        anchorCategory = null,
        siblingCategoryIds = [],
        splittableChildCategories = [],
        splittableChildrenLoading = false,
        categoriesTree = [],
        onSuccess,
        setGlobalLoading,
    } = props;
    const isFromContent = mode === 'fromContent';
    const isBranch = mode === 'branch';
    const anchor = isBranch ? anchorCategory : category;
    const canBranchBottomUp = siblingCategoryIds.length >= 3;
    const canBranchTopDown = splittableChildCategories.length >= 1;

    const [loading, setLoading] = useState(false);
    const setBusy = (busy: boolean) => {
        setLoading(busy);
        setGlobalLoading?.(busy);
    };
    const [suggestedTree, setSuggestedTree] = useState<any>(null);
    const [childSuggestions, setChildSuggestions] = useState<ChildSubtreeSuggestion[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [showContent, setShowContent] = useState(true);
    const [maxDepth, setMaxDepthState] = useState(loadRememberedMaxDepth);
    /** User-driven depth change: remember it for the next time the dialog opens. */
    const setMaxDepth = (depth: number, remember = false) => {
        setMaxDepthState(depth);
        if (remember) saveRememberedMaxDepth(depth);
    };
    const [strategy, setStrategy] = useState<'top_down' | 'bottom_up'>('top_down');
    const [useSuggestedName, setUseSuggestedName] = useState(false);
    const [mergingOccurred, setMergingOccurred] = useState(false);
    const [context, setContext] = useState('');
    const [applyAiAnchorName, setApplyAiAnchorName] = useState(false);
    const [branchOperation, setBranchOperation] =
        useState<BranchOrganizeOperation>('split_children');
    /**
     * Clustering strategy used when generating per content subcategory (Create subcategories).
     * Same Top-down / Bottom-up meaning as for a single content category.
     */
    const [splitChildrenStrategy, setSplitChildrenStrategy] = useState<'top_down' | 'bottom_up'>('top_down');
    const isBranchBottomUp = isBranch && branchOperation === 'group_siblings';
    const isBranchTopDown = isBranch && branchOperation === 'split_children';
    /** Branch top-down: which subcategory suggestions to apply (default: all). */
    const [acceptedChildIds, setAcceptedChildIds] = useState<Set<string>>(() => new Set());
    /** Branch bottom-up: which top-level proposed parent groups to apply (default: all). */
    const [acceptedParentGroupKeys, setAcceptedParentGroupKeys] = useState<Set<string>>(() => new Set());
    const generateAbortRef = useRef<AbortController | null>(null);
    const { getTooltipTextForState } = useHelpContent();
    const theme = useTheme();

    useEffect(() => {
        if (!open) return;
        setSuggestedTree(null);
        setChildSuggestions(null);
        setAcceptedChildIds(new Set());
        setAcceptedParentGroupKeys(new Set());
        setSplitChildrenStrategy('top_down');
        setBranchOperation('split_children');
        setError(null);
        setMergingOccurred(false);
        setApplyAiAnchorName(false);
        setContext('');
        if (preset === 'initialCategories' && isFromContent) {
            setStrategy('bottom_up');
            setMaxDepth(1);
        } else if (isFromContent) {
            setStrategy('top_down');
            setMaxDepth(loadRememberedMaxDepth());
        } else if (isBranch) {
            if (canBranchTopDown || splittableChildrenLoading) {
                setBranchOperation('split_children');
                setSplitChildrenStrategy('top_down');
                setMaxDepth(loadRememberedMaxDepth());
            } else if (canBranchBottomUp) {
                setBranchOperation('group_siblings');
                setSplitChildrenStrategy('top_down');
                setMaxDepth(1);
            } else {
                setBranchOperation('split_children');
                setSplitChildrenStrategy('top_down');
                setMaxDepth(loadRememberedMaxDepth());
            }
        } else {
            setMaxDepth(1);
        }
        // Only re-init when the dialog opens or the entry preset/mode changes — not when
        // splittable-children loading finishes (that was resetting Top-down/Bottom-up).
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
    }, [open, preset, isFromContent, isBranch]);

    // If the active branch strategy becomes unavailable after children resolve, fall back.
    useEffect(() => {
        if (!open || !isBranch || splittableChildrenLoading) return;
        if (branchOperation === 'split_children' && !canBranchTopDown && canBranchBottomUp) {
            setBranchOperation('group_siblings');
            setSplitChildrenStrategy('top_down');
            setMaxDepth(1);
        } else if (branchOperation === 'group_siblings' && !canBranchBottomUp && canBranchTopDown) {
            setBranchOperation('split_children');
            setMaxDepth(loadRememberedMaxDepth());
        }
    }, [
        open,
        isBranch,
        branchOperation,
        canBranchTopDown,
        canBranchBottomUp,
        splittableChildrenLoading,
    ]);

    // Helper function to merge duplicate categories at the same level
    const mergeDuplicateCategories = (node: any): { node: any, merged: boolean } => {
        if (!node || !node.children || node.children.length === 0) {
            return { node, merged: false };
        }

        // Group children by name (for categories) or type
        const categoryGroups = new Map<string, any[]>();
        const contentNodes: any[] = [];
        let hasMerging = false;

        for (const child of node.children) {
            if (child.type === 'category') {
                const name = child.name || 'Unnamed Category';
                if (!categoryGroups.has(name)) {
                    categoryGroups.set(name, []);
                }
                categoryGroups.get(name)!.push(child);
            } else if (child.type === 'content') {
                contentNodes.push(child);
            }
        }

        // Merge categories with the same name
        const mergedCategories: any[] = [];
        for (const [name, categories] of Array.from(categoryGroups.entries())) {
            if (categories.length === 1) {
                // Single category, just merge its children
                const childResult = mergeDuplicateCategories(categories[0]);
                const mergedCategory = {
                    ...categories[0],
                    children: childResult.node.children || []
                };
                mergedCategories.push(mergedCategory);
                if (childResult.merged) hasMerging = true;
            } else {
                // Multiple categories with the same name, merge them
                console.log(`Merging ${categories.length} categories with name: "${name}"`);
                hasMerging = true;
                
                // Collect all children from all categories with this name
                const allChildren: any[] = [];
                for (const category of categories) {
                    if (category.children && category.children.length > 0) {
                        allChildren.push(...category.children);
                    }
                }

                // Create a single merged category
                const childResult = mergeDuplicateCategories({ children: allChildren });
                const mergedCategory = {
                    name: name,
                    type: 'category',
                    children: childResult.node.children || []
                };
                mergedCategories.push(mergedCategory);
                if (childResult.merged) hasMerging = true;
            }
        }

        // Return the node with merged children
        return {
            node: {
                ...node,
                children: [...mergedCategories, ...contentNodes]
            },
            merged: hasMerging
        };
    };

    /** Server-side apply (one document lock; same createCategory/createContent as outline and move). */
    const applySuggestedTreeOnServer = async (
        anchorCategoryId: string,
        tree: any,
        withContent: boolean,
    ): Promise<{ tempStashCategoryId?: string }> => {
        const response = await api.post('/categories/apply-suggested-tree-from-content', {
            categoryId: anchorCategoryId,
            tree,
            withContent,
            strategy,
            maxDepth,
        });
        const tempStashCategoryId = response.data?.tempStashCategoryId as string | undefined;
        return { tempStashCategoryId };
    };

    const applyInitialCategoriesPreset = () => {
        setStrategy('bottom_up');
        setSplitChildrenStrategy('top_down');
        setMaxDepth(1);
        setSuggestedTree(null);
        setChildSuggestions(null);
        setAcceptedChildIds(new Set());
        setAcceptedParentGroupKeys(new Set());
        setError(null);
    };

    const clearBranchPreview = () => {
        setSuggestedTree(null);
        setChildSuggestions(null);
        setAcceptedChildIds(new Set());
        setAcceptedParentGroupKeys(new Set());
        setMergingOccurred(false);
    };

    const handleGenerateFromContent = async () => {
        if (!category) return;
        try {
            setBusy(true);
            setError(null);
            const result = await suggestTreeFromCategory(category.id, maxDepth, context, strategy);
            const mergeResult = mergeDuplicateCategories(result.tree);
            if (strategy === 'bottom_up') {
                flattenSuggestedCategoryChildrenToContentOnly(mergeResult.node);
            }
            setMergingOccurred(mergeResult.merged);
            setSuggestedTree({ ...result, tree: mergeResult.node });
        } catch (err: any) {
            setError(err.message || 'Failed to generate category structure');
        } finally {
            setBusy(false);
        }
    };

    const handleRetryFromContent = async () => {
        if (!category) return;
        try {
            setBusy(true);
            setError(null);
            setMergingOccurred(false);
            const result = await suggestTreeFromCategory(category.id, maxDepth, context, strategy);
            const mergeResult = mergeDuplicateCategories(result.tree);
            if (strategy === 'bottom_up') {
                flattenSuggestedCategoryChildrenToContentOnly(mergeResult.node);
            }
            setMergingOccurred(mergeResult.merged);
            setSuggestedTree({ ...result, tree: mergeResult.node });
        } catch (err: any) {
            setError(err.message || 'Failed to regenerate category structure');
        } finally {
            setBusy(false);
        }
    };

    const handleGenerateSplitChildren = async () => {
        if (splittableChildCategories.length === 0) return;
        generateAbortRef.current?.abort();
        const abortController = new AbortController();
        generateAbortRef.current = abortController;
        const { signal } = abortController;
        setBusy(true);
        setError(null);
        setMergingOccurred(false);
        try {
            const results: ChildSubtreeSuggestion[] = [];
            let anyMerged = false;
            for (const child of splittableChildCategories) {
                if (signal.aborted) return;
                const result = await suggestTreeFromCategory(
                    child.id,
                    maxDepth,
                    context,
                    splitChildrenStrategy,
                );
                const mergeResult = mergeDuplicateCategories(result.tree);
                if (splitChildrenStrategy === 'bottom_up') {
                    flattenSuggestedCategoryChildrenToContentOnly(mergeResult.node);
                }
                if (mergeResult.merged) anyMerged = true;
                results.push({
                    categoryId: child.id,
                    categoryName: child.name,
                    suggested: { ...result, tree: mergeResult.node },
                });
            }
            setChildSuggestions(results);
            setAcceptedChildIds(new Set(results.map((r) => r.categoryId)));
            setSuggestedTree(null);
            setMergingOccurred(anyMerged);
        } catch (err: any) {
            if (axios.isCancel(err)) return;
            setError(err.message || 'Failed to generate structure for subcategories');
            setChildSuggestions(null);
            setAcceptedChildIds(new Set());
        } finally {
            if (generateAbortRef.current === abortController) {
                generateAbortRef.current = null;
            }
            setBusy(false);
        }
    };

    const handleGenerateGroupSiblings = async () => {
        if (!anchor || siblingCategoryIds.length < 2) return;
        generateAbortRef.current?.abort();
        const abortController = new AbortController();
        generateAbortRef.current = abortController;
        const { signal } = abortController;
        setBusy(true);
        setError(null);
        try {
            const data = await suggestParentStructureOverCategories(
                siblingCategoryIds,
                maxDepth,
                context.trim(),
                signal,
            );
            setSuggestedTree(data);
            const groupKeys = new Set<string>();
            (data?.tree?.children || []).forEach((sub: any, i: number) => {
                if (sub?.type === 'category') groupKeys.add(parentGroupKey(sub, i));
            });
            setAcceptedParentGroupKeys(groupKeys);
            setChildSuggestions(null);
            setAcceptedChildIds(new Set());
        } catch (e: any) {
            if (axios.isCancel(e)) return;
            const timedOut =
                axios.isAxiosError(e) &&
                (e.code === 'ECONNABORTED' ||
                    String(e.message || '')
                        .toLowerCase()
                        .includes('timeout'));
            const minutes = Math.max(1, Math.round(SUGGEST_TREE_REQUEST_TIMEOUT_MS / 60_000));
            const msg = timedOut
                ? `The request timed out after about ${minutes} minutes. Try fewer siblings or a lower max height.`
                : axios.isAxiosError(e) && e.response?.data
                  ? typeof e.response.data === 'string'
                      ? e.response.data
                      : (e.response.data as any)?.message || e.message
                  : e instanceof Error
                    ? e.message
                    : 'Failed to generate structure';
            setError(String(msg));
            setSuggestedTree(null);
            setAcceptedParentGroupKeys(new Set());
        } finally {
            if (generateAbortRef.current === abortController) {
                generateAbortRef.current = null;
            }
            setBusy(false);
        }
    };

    const historyOperationType =
        isBranchBottomUp
            ? 'proposeParentCategories'
            : preset === 'initialCategories' || (strategy === 'bottom_up' && maxDepth === 1)
              ? 'generateInitialCategories'
              : 'generateSubtree';

    const applySuggestionToContentCategory = async (
        cat: Category,
        suggestion: { tree: any; originalContents?: SuggestTreeOriginalContent[] },
        withContent: boolean,
        operationType: 'generateSubtree' | 'generateInitialCategories',
    ): Promise<string> => {
        const originalContents = cat.contents || [];
        const { tempStashCategoryId } = await applySuggestedTreeOnServer(
            cat.id,
            suggestion.tree,
            withContent,
        );
        const realGeneratedTree = await fetchCategoryWithChildren(cat.id);
        historyService.recordOperation({
            type: operationType,
            data: {
                originalCategory: cat,
                originalContents,
                tempCategory: tempStashCategoryId
                    ? { id: tempStashCategoryId, name: `Original Content - ${cat.name}` } as Category
                    : undefined,
                generatedTree: realGeneratedTree,
            },
        });
        return cat.id;
    };

    const handleApplySplitChildren = async (withContent: boolean) => {
        if (!childSuggestions?.length || !anchor) return;
        const selected = childSuggestions.filter((item) => acceptedChildIds.has(item.categoryId));
        if (selected.length === 0) {
            setError('Select at least one subcategory to apply.');
            return;
        }
        const operationType =
            splitChildrenStrategy === 'bottom_up' && maxDepth === 1
                ? 'generateInitialCategories'
                : 'generateSubtree';
        setBusy(true);
        setError(null);
        try {
            if (selected.length > 1) {
                historyService.startBatchOperation(operationType);
            }
            for (const item of selected) {
                const fresh = await fetchCategoryWithChildren(item.categoryId);
                if (!fresh?.contentCategory) continue;
                await applySuggestionToContentCategory(fresh, item.suggested, withContent, operationType);
            }
            if (selected.length > 1) {
                historyService.endBatchOperation();
            }
            setChildSuggestions(null);
            setAcceptedChildIds(new Set());
            onSuccess(anchor.id);
            onClose();
        } catch (err: any) {
            setError(
                err.message ||
                    (withContent
                        ? 'Failed to apply structure to subcategories'
                        : 'Failed to apply structure-only to subcategories'),
            );
        } finally {
            setBusy(false);
        }
    };

    const handleApplySplitChildrenWithContent = () => handleApplySplitChildren(true);
    const handleApplySplitChildrenStructureOnly = () => handleApplySplitChildren(false);

    const toggleAcceptedChild = (categoryId: string) => {
        setAcceptedChildIds((prev) => {
            const next = new Set(prev);
            if (next.has(categoryId)) next.delete(categoryId);
            else next.add(categoryId);
            return next;
        });
    };

    const toggleAcceptedParentGroup = (key: string) => {
        setAcceptedParentGroupKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const handleAcceptWithContent = async () => {
        if (!category || !suggestedTree) return;
        
        console.log('handleAcceptWithContent called with useSuggestedName:', useSuggestedName);
        
        setBusy(true);
        setError(null);
        
        try {
            const originalContents = category.contents || [];

            // Phase 1 (optional): Rename before apply
            let targetCategoryId = category.id;
            let renameSuccessful = false;
            
            if (useSuggestedName && suggestedTree.tree.name && suggestedTree.tree.name !== 'root') {
                console.log('Phase 2: Checking rename conditions...');
                
                // Check if we can rename this category (not a root category)
                if (category.parentId) {
                    console.log('✓ Category can be renamed - attempting rename');
                    console.log('Category details:', { 
                        categoryId: category.id, 
                        categoryName: category.name, 
                        categoryParentId: category.parentId,
                        newName: suggestedTree.tree.name
                    });
                    
                    try {
                        console.log('Making API call to rename category...');
                        const response = await api.put('/categories', {
                            parentId: category.id,
                            name: suggestedTree.tree.name,
                            description: category.description || ''
                        });
                        
                        console.log('✓ Successfully renamed category to:', suggestedTree.tree.name, 'Response:', response.data);
                        
                        // Use the updated category ID from the response
                        targetCategoryId = response.data.id;
                        renameSuccessful = true;
                        console.log('✓ Rename successful, new category ID:', targetCategoryId);
                    } catch (err: any) {
                        console.error('✗ Failed to rename category with suggested name:', err);
                        // Continue with original category ID since rename failed
                        targetCategoryId = category.id;
                        renameSuccessful = false;
                        console.log('Continuing with original category ID:', targetCategoryId);
                    }
                } else {
                    console.log('✗ Cannot rename root category, using original ID');
                    setUseSuggestedName(false);
                }
            } else {
                console.log('✗ Skipping rename - conditions not met');
            }

            await applySuggestedTreeOnServer(targetCategoryId, suggestedTree.tree, true);
            const realGeneratedTree = await fetchCategoryWithChildren(targetCategoryId);
            historyService.recordOperation({
                type: historyOperationType,
                data: {
                    originalCategory: category,
                    originalContents,
                    tempCategory: undefined,
                    generatedTree: realGeneratedTree,
                    renamedCategoryId: renameSuccessful ? targetCategoryId : undefined,
                    originalCategoryName: renameSuccessful ? category.name : undefined,
                },
            });
            onSuccess(renameSuccessful ? targetCategoryId : category.id);
            setSuggestedTree(null); // Clear the suggested tree
            onClose();
        } catch (err: any) {
            setError(err.message || 'Failed to apply layout with content');
        } finally {
            setBusy(false);
        }
    };

    const handleAcceptStructureOnly = async () => {
        if (!suggestedTree || !category) return;
        
        setBusy(true);
        setError(null);
        
        try {
            let targetCategoryId = category.id;
            let renameSuccessful = false;
            let newCategoryId = category.id; // Store the new category ID separately

            // Rename before apply (server stashes rows into a temp bucket when structure-only)
            if (useSuggestedName && suggestedTree.tree.name && suggestedTree.tree.name !== 'root') {
                console.log('Phase 2: Checking rename conditions...');
                
                // Check if we can rename this category (not a root category)
                if (category.parentId) {
                    console.log('✓ Category can be renamed - attempting rename');
                    console.log('Category details:', { 
                        categoryId: category.id, 
                        categoryName: category.name, 
                        categoryParentId: category.parentId,
                        newName: suggestedTree.tree.name
                    });
                    
                    try {
                        console.log('Making API call to rename category...');
                        const response = await api.put('/categories', {
                            parentId: category.id,
                            name: suggestedTree.tree.name,
                            description: category.description || ''
                        });
                        
                        console.log('✓ Successfully renamed category to:', suggestedTree.tree.name, 'Response:', response.data);
                        
                        // Use the updated category ID from the response
                        newCategoryId = response.data.id; // Store in separate variable
                        targetCategoryId = newCategoryId; // Use for tree creation
                        renameSuccessful = true;
                        console.log('✓ Rename successful, new category ID:', newCategoryId);
                    } catch (err: any) {
                        console.error('✗ Failed to rename category with suggested name:', err);
                        // Continue with original category ID since rename failed
                        targetCategoryId = category.id;
                        newCategoryId = category.id;
                        renameSuccessful = false;
                        console.log('Continuing with original category ID:', targetCategoryId);
                    }
                } else {
                    console.log('✗ Cannot rename root category, using original ID');
                    setUseSuggestedName(false);
                }
            } else {
                console.log('✗ Skipping rename - conditions not met');
            }
            
            const { tempStashCategoryId } = await applySuggestedTreeOnServer(
                targetCategoryId,
                suggestedTree.tree,
                false,
            );

            const realGeneratedTree = await fetchCategoryWithChildren(targetCategoryId);

            const operationData = {
                originalCategory: category,
                originalContents: category.contents || [],
                tempCategory: tempStashCategoryId
                    ? { id: tempStashCategoryId, name: `Original Content - ${category.name}` } as Category
                    : undefined,
                generatedTree: realGeneratedTree,
                renamedCategoryId: renameSuccessful ? newCategoryId : undefined,
                originalCategoryName: renameSuccessful ? category.name : undefined
            };

            console.log('Recording generateSubtree operation for undo/redo:', operationData);
            historyService.recordOperation({
                type: historyOperationType,
                data: operationData
            });
            
            // Pass the updated category ID to the success callback
            // If rename was successful, use the new ID; otherwise use the original ID
            const finalCategoryId = renameSuccessful ? newCategoryId : category.id;
            onSuccess(finalCategoryId);
            setSuggestedTree(null); // Clear the suggested tree
            onClose();
        } catch (err: any) {
            setError(err.message || 'Failed to create subcategory structure');
        } finally {
            setBusy(false);
        }
    };

    const stripBomAndTrimCategoryId = (id: string) => id.replace(/^\uFEFF/g, '').trim();
    const proposeParentFormHeaders = { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } };
    const putCategoryMoveForm = (categoryId: string, newParentId: string) =>
        api.put(
            '/categories/move',
            new URLSearchParams({ categoryId, newParentId }).toString(),
            proposeParentFormHeaders,
        );
    const fetchCategoryById = (categoryId: string) => {
        const qs = new URLSearchParams();
        qs.set('categoryId', stripBomAndTrimCategoryId(categoryId));
        return api.get<Category>(`/categories?${qs.toString()}`);
    };

    const seedIdMapFromProposePreviewRoot = (previewRoot: { children?: any[] }) => {
        const map = new Map<string, string>();
        const walk = (node: any) => {
            if (!node || node.type !== 'category') return;
            const ch = node.children || [];
            if (ch.length === 1 && ch[0]?.type === 'content' && typeof ch[0]?.text === 'string') {
                const m = ch[0].text.match(SOURCE_CAT_MARKER);
                if (m?.[1]) map.set(stripBomAndTrimCategoryId(m[1]), stripBomAndTrimCategoryId(m[1]));
            }
            for (const sub of ch) {
                if (sub?.type === 'category') walk(sub);
            }
        };
        for (const sub of previewRoot?.children || []) {
            if (sub?.type === 'category') walk(sub);
        }
        return map;
    };

    const applySuggestParentNode = async (
        parentId: string,
        node: any,
        idMap: Map<string, string>,
        tracking: { createdCategoryIds: string[]; movesInApplyOrder: { originalId: string; previousParentId: string }[] },
    ): Promise<void> => {
        if (!node || node.type !== 'category') return;
        const ch = node.children || [];
        if (
            ch.length === 1 &&
            ch[0]?.type === 'content' &&
            typeof ch[0]?.text === 'string' &&
            ch[0].text.includes('__SRC_CAT__=')
        ) {
            const m = ch[0].text.match(SOURCE_CAT_MARKER);
            if (!m?.[1]) return;
            const origFromMarker = stripBomAndTrimCategoryId(m[1]);
            const catId = stripBomAndTrimCategoryId(idMap.get(origFromMarker) ?? origFromMarker);
            const moveTargetParentId = stripBomAndTrimCategoryId(parentId);
            let prevParentId = findCategoryInTree(categoriesTree, catId)?.parentId;
            if (typeof prevParentId !== 'string' || !prevParentId) {
                const catGet = await fetchCategoryById(catId);
                prevParentId = catGet.data?.parentId;
            }
            if (typeof prevParentId !== 'string' || !prevParentId) {
                throw new Error(`Could not resolve previous parent for category ${catId}`);
            }
            tracking.movesInApplyOrder.push({
                originalId: origFromMarker,
                previousParentId: stripBomAndTrimCategoryId(prevParentId),
            });
            const moveRes = await putCategoryMoveForm(catId, moveTargetParentId);
            const moved = moveRes.data as Category;
            if (moved?.id) idMap.set(origFromMarker, moved.id);
            return;
        }
        const res = await api.post('/categories', {
            parentId: stripBomAndTrimCategoryId(parentId),
            name: normalizePresentation((node.name || 'New category').trim()) || 'New category',
            description: '',
        });
        const newPid = res.data.id as string;
        tracking.createdCategoryIds.push(newPid);
        for (const sub of ch) {
            if (sub?.type === 'category') {
                await applySuggestParentNode(newPid, sub, idMap, tracking);
            }
        }
    };

    const handleApplyGroupSiblings = async () => {
        if (!anchor || !suggestedTree?.tree) return;
        try {
            setBusy(true);
            setError(null);
            const anchorLive = findCategoryInTree(categoriesTree, anchor.id);
            if (!anchorLive) {
                setError('The selected category is not in the current category structure. Reload the document and try again.');
                return;
            }
            const anchorIdOriginal = anchorLive.id;
            const root = suggestedTree.tree;
            const tracking = { createdCategoryIds: [] as string[], movesInApplyOrder: [] as { originalId: string; previousParentId: string }[] };
            let optionalAnchorRename:
                | { previousName: string; previousDescription: string; newName: string }
                | undefined;
            if (
                applyAiAnchorName &&
                anchorLive.parentId &&
                typeof root.name === 'string' &&
                root.name.trim() &&
                root.name.trim() !== anchorLive.name
            ) {
                optionalAnchorRename = {
                    previousName: anchorLive.name,
                    previousDescription: anchorLive.description || '',
                    newName: root.name.trim(),
                };
            }
            const workAnchorId = stripBomAndTrimCategoryId(anchorIdOriginal);
            const selectedChildren = (root.children || []).filter(
                (sub: any, i: number) =>
                    sub?.type === 'category' && acceptedParentGroupKeys.has(parentGroupKey(sub, i)),
            );
            if (selectedChildren.length === 0) {
                setError('Select at least one proposed parent group to apply.');
                return;
            }
            const selectedPreviewRoot = { ...root, children: selectedChildren };
            const idMap = seedIdMapFromProposePreviewRoot(selectedPreviewRoot);
            const groupedCategoryIds = Array.from(idMap.keys());
            for (const sub of selectedChildren) {
                await applySuggestParentNode(workAnchorId, sub, idMap, tracking);
            }
            let anchorIdAfterApply = workAnchorId;
            if (optionalAnchorRename) {
                const anchorBeforeUmbrellaRename = anchorIdAfterApply;
                const ren = await api.put('/categories', {
                    parentId: anchorIdAfterApply,
                    name:
                        normalizePresentation(optionalAnchorRename.newName.trim()) ||
                        optionalAnchorRename.newName,
                    description: optionalAnchorRename.previousDescription,
                });
                anchorIdAfterApply = stripBomAndTrimCategoryId((ren.data as Category).id);
                const oldA = stripBomAndTrimCategoryId(anchorBeforeUmbrellaRename);
                const newA = stripBomAndTrimCategoryId(anchorIdAfterApply);
                if (oldA !== newA) {
                    const rebasePath = (p: string) => {
                        const v = stripBomAndTrimCategoryId(p);
                        if (v === oldA) return newA;
                        if (v.startsWith(`${oldA}:`)) return newA + v.slice(oldA.length);
                        return v;
                    };
                    for (const [k, v] of Array.from(idMap.entries())) {
                        idMap.set(k, rebasePath(v));
                    }
                    for (let i = 0; i < tracking.createdCategoryIds.length; i++) {
                        tracking.createdCategoryIds[i] = rebasePath(tracking.createdCategoryIds[i]);
                    }
                }
            }
            historyService.recordOperation({
                type: 'proposeParentCategories',
                data: {
                    anchorCategoryId: anchorIdAfterApply,
                    anchorCategoryIdBeforeApply: anchorIdOriginal,
                    previewTreeRoot: selectedPreviewRoot,
                    groupedCategoryIds,
                    createdCategoryIds: tracking.createdCategoryIds,
                    movesInApplyOrder: tracking.movesInApplyOrder,
                    finalIdsByOriginalId: Object.fromEntries(idMap),
                    optionalAnchorRename,
                },
            });
            setSuggestedTree(null);
            setAcceptedParentGroupKeys(new Set());
            onSuccess(anchorIdAfterApply);
            onClose();
        } catch (err: any) {
            setError(err instanceof Error ? err.message : 'Failed to apply suggested structure');
        } finally {
            setBusy(false);
        }
    };

    const renderGroupSiblingsPreview = (node: any, depth = 0, isPreviewRoot = false): React.ReactNode => {
        if (!node || node.type !== 'category') return null;
        const displayName = isPreviewRoot && anchor ? anchor.name : node.name;
        const aiTheme =
            isPreviewRoot && anchor && typeof node.name === 'string' && node.name !== anchor.name
                ? node.name
                : null;
        return (
            <Box key={`${depth}-${displayName}`} sx={{ pl: depth * 2 }}>
                <Typography variant="body2">📁 {displayName}</Typography>
                {aiTheme && (
                    <Typography variant="caption" color="text.secondary" display="block">
                        Suggested umbrella title: {aiTheme}
                    </Typography>
                )}
                {(node.children || []).map((sub: any, i: number) => {
                    if (sub?.type !== 'category') return null;
                    if (isPreviewRoot) {
                        const key = parentGroupKey(sub, i);
                        return (
                            <Box
                                key={key}
                                sx={{ mt: 1, opacity: acceptedParentGroupKeys.has(key) ? 1 : 0.5 }}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={acceptedParentGroupKeys.has(key)}
                                            onChange={() => toggleAcceptedParentGroup(key)}
                                            disabled={loading}
                                            size="small"
                                        />
                                    }
                                    label={
                                        <Typography variant="body2" component="span">
                                            📁 {sub.name}
                                        </Typography>
                                    }
                                    sx={{ alignItems: 'center', ml: 0 }}
                                />
                                <Box sx={{ pl: 4 }}>
                                    {(sub.children || []).map((child: any, j: number) =>
                                        child?.type === 'category'
                                            ? renderGroupSiblingsPreview(child, depth + 2, false)
                                            : null,
                                    )}
                                </Box>
                            </Box>
                        );
                    }
                    return renderGroupSiblingsPreview(sub, depth + 1, false);
                })}
            </Box>
        );
    };

    const handleClose = () => {
        generateAbortRef.current?.abort();
        generateAbortRef.current = null;
        setSuggestedTree(null);
        setChildSuggestions(null);
        setAcceptedChildIds(new Set());
        setAcceptedParentGroupKeys(new Set());
        setError(null);
        setShowContent(true);
        setMaxDepth(loadRememberedMaxDepth());
        setStrategy('top_down');
        setUseSuggestedName(false);
        setMergingOccurred(false);
        setContext('');
        setApplyAiAnchorName(false);
        setSplitChildrenStrategy('top_down');
        setBranchOperation('split_children');
        onClose();
    };

    // Helper to detect RTL (Hebrew/Arabic)
    const isRTL = suggestedTree && suggestedTree.tree && /[\u0590-\u05FF\u0600-\u06FF]/.test(suggestedTree.tree.name);

    // Helper function to detect content categories
    const isContentCategory = (node: any): boolean => {
        if (!node.children || node.children.length === 0) {
            return true; // No children = content category
        }
        // Has children of type 'content' = content category
        return node.children.some((child: any) => child.type === 'content');
    };

    // Helper to render tree preview
    const renderTree = (
        node: any,
        path: string = '',
        level: number = 0,
        usedIndices: Set<number> = new Set(),
        originalContentsOverride?: SuggestTreeOriginalContent[],
    ): React.ReactNode[] => {
        const originalContentsForLeaves = originalContentsOverride ?? suggestedTree?.originalContents;
        if (!node) return [];
        const currentPath = path + (node.name || node.text || '');
        const padding = isRTL ? { pr: level * 2 } : { pl: level * 2 };

        // Category node (including root)
        if (node.type === 'category' && node.name) {
            const children = Array.isArray(node.children)
                ? node.children.flatMap((child: any, idx: number) =>
                    renderTree(child, currentPath + '-' + idx, level + 1, usedIndices, originalContentsForLeaves))
                : [];
            const isContentCat = isContentCategory(node);
            const iconColor = isContentCat ? theme.palette.info.main : theme.palette.primary.main;
            return [
                <Box key={currentPath} sx={{ display: 'flex', alignItems: 'center', ...padding, mb: 0.5 }} dir={isRTL ? 'rtl' : 'ltr'}>
                    {isContentCat ? (
                        <DescriptionIcon sx={{ color: iconColor, mr: isRTL ? 0 : 1, ml: isRTL ? 1 : 0 }} />
                    ) : (
                    <FolderIcon sx={{ color: iconColor, mr: isRTL ? 0 : 1, ml: isRTL ? 1 : 0 }} />
                    )}
                    <Typography variant="body1" sx={{ fontWeight: 700, wordBreak: 'break-word', textAlign: isRTL ? 'right' : 'left', flex: 1 }} dir="auto">
                        {node.name}
                    </Typography>
                </Box>,
                ...children
            ];
        }

        // Content node
        if (showContent && node.type === 'content') {
            const resolved = resolveOriginalContentForLeaf(
                node,
                originalContentsForLeaves,
                usedIndices,
            );
            const displayText = resolved?.text ?? '';
            const subcaseText = resolved?.subcase ?? '';

            if (displayText || subcaseText) {
                return [
                    <Box key={currentPath} sx={{ display: 'flex', flexDirection: 'column', ...padding, mb: 0.5 }} dir={isRTL ? 'rtl' : 'ltr'}>
                        {subcaseText && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                <StickyNote2OutlinedIcon sx={{ color: 'warning.main', mr: isRTL ? 0 : 1, ml: isRTL ? 1 : 0, fontSize: 18, mt: 0.5 }} />
                                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600, wordBreak: 'break-word', textAlign: isRTL ? 'right' : 'left' }} dir="auto">
                                    {subcaseText}
                                </Typography>
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', wordBreak: 'break-word', whiteSpace: 'pre-wrap', lineHeight: 1.5, textAlign: isRTL ? 'right' : 'left', flex: 1 }} dir="auto">
                                {displayText}
                            </Typography>
                        </Box>
                    </Box>
                ];
            }
        }

        return [];
    };

    return (
        <Dialog 
            open={open} 
            onClose={(_event, reason) => {
                if (reason === 'backdropClick') return;
                handleClose();
            }}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                Structure categories (AI)
                {anchor && (
                    <Typography variant="subtitle2" color="text.secondary">
                        {isBranch
                            ? `Branch: ${anchor.name}`
                            : `Create subcategories under “${anchor.name}”`}
                    </Typography>
                )}
            </DialogTitle>
            
            <DialogContent sx={{ position: 'relative' }}>
                {!suggestedTree && !childSuggestions && (
                    <Box sx={{ mb: 2 }}>
                        {isFromContent && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                <Alert severity="info" sx={{ width: '100%', py: 0.5 }}>
                                    Builds a new folder structure under this content category. Choose how the AI should
                                    group its rows.
                                </Alert>
                                <FormControl component="fieldset" variant="standard" sx={{ width: '100%' }}>
                                    <FormLabel component="legend">
                                        <Tooltip title={getTooltipTextForState('structure-categories-strategy', true)} placement="top">
                                            <span>Building strategy</span>
                                        </Tooltip>
                                    </FormLabel>
                                    <ToggleButtonGroup
                                        exclusive
                                        size="small"
                                        value={strategy}
                                        onChange={(_, v) => {
                                            if (!v) return;
                                            setStrategy(v);
                                            // Leaving Bottom-up / Initial categories preset returns clustering to top-down.
                                            setSplitChildrenStrategy('top_down');
                                            clearBranchPreview();
                                        }}
                                        sx={{ mt: 0.5 }}
                                    >
                                        <ToggleButton value="top_down">
                                            <Tooltip
                                                title={getTooltipTextForState('structure-categories-strategy-top-down', true)}
                                                placement="top"
                                            >
                                                <span>Top-down</span>
                                            </Tooltip>
                                        </ToggleButton>
                                        <ToggleButton value="bottom_up">
                                            <Tooltip
                                                title={getTooltipTextForState('structure-categories-strategy-bottom-up', true)}
                                                placement="top"
                                            >
                                                <span>Bottom-up</span>
                                            </Tooltip>
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                </FormControl>
                                <Tooltip
                                    title={getTooltipTextForState('generate-initial-categories', true)}
                                    placement="top"
                                >
                                    <span>
                                        <Button
                                            size="small"
                                            variant={
                                                strategy === 'bottom_up' && maxDepth === 1
                                                    ? 'contained'
                                                    : 'outlined'
                                            }
                                            onClick={applyInitialCategoriesPreset}
                                            disabled={loading}
                                        >
                                            Initial categories (depth 1, bottom-up)
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Box>
                        )}

                        {isBranch && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                                <FormControl component="fieldset" variant="standard" sx={{ width: '100%' }}>
                                    <FormLabel component="legend">
                                        Operation
                                    </FormLabel>
                                    <ToggleButtonGroup
                                        exclusive
                                        orientation="vertical"
                                        size="small"
                                        value={branchOperation}
                                        onChange={(_, v: BranchOrganizeOperation | null) => {
                                            if (!v) return;
                                            setBranchOperation(v);
                                            setSplitChildrenStrategy('top_down');
                                            setMaxDepth(v === 'group_siblings' ? 1 : loadRememberedMaxDepth());
                                            clearBranchPreview();
                                        }}
                                        sx={{
                                            mt: 0.5,
                                            alignItems: 'stretch',
                                            '& .MuiToggleButton-root': {
                                                justifyContent: 'flex-start',
                                                textAlign: 'left',
                                                px: 1.5,
                                                py: 1,
                                            },
                                        }}
                                    >
                                        <ToggleButton
                                            value="split_children"
                                            disabled={!canBranchTopDown && !splittableChildrenLoading}
                                        >
                                            <Box>
                                                <Typography variant="subtitle2">
                                                    Create subcategories
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Build a new structure under each selected content category.
                                                </Typography>
                                            </Box>
                                        </ToggleButton>
                                        <ToggleButton
                                            value="group_siblings"
                                            disabled={!canBranchBottomUp}
                                        >
                                            <Box>
                                                <Typography variant="subtitle2">
                                                    Create parent groups
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Build new parent folders above the selected sibling categories.
                                                </Typography>
                                            </Box>
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                </FormControl>
                                {splittableChildrenLoading && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 0.75 }}
                                    >
                                        <CircularProgress size={12} />
                                        Checking subcategories…
                                    </Typography>
                                )}
                                {!splittableChildrenLoading && !canBranchTopDown && canBranchBottomUp && (
                                    <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
                                        Create subcategories needs content subcategories with at least two rows.
                                    </Typography>
                                )}
                                {!splittableChildrenLoading && !canBranchBottomUp && (
                                    <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
                                        Create parent groups needs three or more siblings in scope (leave none checked
                                        to include all, or check three or more).
                                    </Typography>
                                )}
                                <Alert severity="info" sx={{ py: 0.5 }}>
                                    {branchOperation === 'group_siblings'
                                        ? `Will reorganize ${siblingCategoryIds.length} sibling categories under new parent folders.`
                                        : `Will build a separate ${splitChildrenStrategy === 'bottom_up' ? 'bottom-up' : 'top-down'} structure under ${splittableChildCategories.length} content ${splittableChildCategories.length === 1 ? 'category' : 'categories'}.`}
                                </Alert>
                                {branchOperation === 'split_children' && (
                                    <FormControl component="fieldset" variant="standard" sx={{ width: '100%' }}>
                                        <FormLabel component="legend">
                                            <Tooltip
                                                title={getTooltipTextForState('structure-categories-strategy', true)}
                                                placement="top"
                                            >
                                                <span>Building strategy</span>
                                            </Tooltip>
                                        </FormLabel>
                                        <ToggleButtonGroup
                                            exclusive
                                            size="small"
                                            value={splitChildrenStrategy}
                                            onChange={(_, v) => {
                                                if (!v) return;
                                                setSplitChildrenStrategy(v);
                                                clearBranchPreview();
                                            }}
                                            disabled={!canBranchTopDown && !splittableChildrenLoading}
                                            sx={{ mt: 0.5 }}
                                        >
                                            <ToggleButton value="top_down">
                                                <Tooltip
                                                    title={getTooltipTextForState(
                                                        'structure-categories-strategy-top-down',
                                                        true,
                                                    )}
                                                    placement="top"
                                                >
                                                    <span>Top-down</span>
                                                </Tooltip>
                                            </ToggleButton>
                                            <ToggleButton value="bottom_up">
                                                <Tooltip
                                                    title={getTooltipTextForState(
                                                        'structure-categories-strategy-bottom-up',
                                                        true,
                                                    )}
                                                    placement="top"
                                                >
                                                    <span>Bottom-up</span>
                                                </Tooltip>
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                    </FormControl>
                                )}
                            </Box>
                        )}

                        <Tooltip title={getTooltipTextForState('suggest-tree-context', true)} placement="top">
                            <TextField
                                fullWidth
                                label="Context (optional)"
                                value={context}
                                onChange={(e) => setContext(e.target.value)}
                                placeholder="e.g. legal contracts; organize by theme"
                                multiline
                                rows={3}
                                margin="normal"
                            />
                        </Tooltip>

                        <Typography variant="body2" sx={{ mt: 2, mb: 0.5 }}>
                            {isBranchBottomUp ? 'Max height (new parent levels)' : 'Maximum depth'}: {maxDepth}
                        </Typography>
                        <Tooltip title={getTooltipTextForState('structure-categories-max-depth', true)} placement="top">
                            <Slider
                                value={maxDepth}
                                onChange={(_, value) =>
                                    setMaxDepth(value as number, !isBranchBottomUp)
                                }
                                min={1}
                                max={5}
                                marks
                                valueLabelDisplay="auto"
                                sx={{ mb: 2 }}
                            />
                        </Tooltip>
                        <Button
                            variant="contained"
                            onClick={
                                isBranchBottomUp
                                    ? handleGenerateGroupSiblings
                                    : isBranchTopDown
                                      ? handleGenerateSplitChildren
                                      : handleGenerateFromContent
                            }
                            disabled={
                                loading ||
                                (isFromContent && !category) ||
                                (isBranchBottomUp && (!anchor || siblingCategoryIds.length < 3)) ||
                                (isBranchTopDown && splittableChildrenLoading) ||
                                (isBranchTopDown && !splittableChildrenLoading && splittableChildCategories.length === 0)
                            }
                        >
                            Generate preview
                        </Button>
                    </Box>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {mergingOccurred && (isFromContent || isBranchTopDown) && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Some categories with duplicate names were automatically merged to maintain a valid structure.
                    </Alert>
                )}

                {suggestedTree && isFromContent && (
                    <Box>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            Suggested Structure
                        </Typography>
                        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <ToggleButtonGroup
                                exclusive
                                size="small"
                                value={strategy}
                                onChange={(_, v) => v && setStrategy(v)}
                                disabled={loading}
                            >
                                <ToggleButton value="top_down">
                                    <Tooltip
                                        title={getTooltipTextForState('structure-categories-strategy-top-down', true)}
                                        placement="top"
                                    >
                                        <span>Top-down</span>
                                    </Tooltip>
                                </ToggleButton>
                                <ToggleButton value="bottom_up">
                                    <Tooltip
                                        title={getTooltipTextForState('structure-categories-strategy-bottom-up', true)}
                                        placement="top"
                                    >
                                        <span>Bottom-up</span>
                                    </Tooltip>
                                </ToggleButton>
                            </ToggleButtonGroup>
                            <Box sx={{ minWidth: 160, flex: 1 }}>
                                <Typography variant="body2" gutterBottom>
                                    Depth: {maxDepth}
                                </Typography>
                                <Slider
                                    value={maxDepth}
                                    onChange={(_, value) => setMaxDepth(value as number, true)}
                                    min={1}
                                    max={5}
                                    marks
                                    valueLabelDisplay="auto"
                                    disabled={loading}
                                />
                            </Box>
                            <Button variant="contained" onClick={handleRetryFromContent} disabled={loading}>
                                Retry
                            </Button>
                        </Box>
                        <FormControlLabel
                            control={
                                <Checkbox checked={showContent} onChange={(e) => setShowContent(e.target.checked)} />
                            }
                            label="Show content preview"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={useSuggestedName}
                                    onChange={(e) => setUseSuggestedName(e.target.checked)}
                                    disabled={!category?.parentId}
                                />
                            }
                            label={
                                !category?.parentId
                                    ? `Cannot rename root category: "${suggestedTree.tree.name}"`
                                    : `Use AI-suggested name: "${suggestedTree.tree.name}"`
                            }
                        />
                        <Box
                            sx={{
                                maxHeight: 400,
                                overflow: 'auto',
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                p: 2,
                                mt: 2,
                                bgcolor: (t) =>
                                    t.palette.mode === 'dark'
                                        ? alpha(t.palette.common.white, 0.04)
                                        : alpha(t.palette.common.black, 0.02),
                            }}
                        >
                            {renderTree(suggestedTree.tree, '', 0, new Set())}
                        </Box>
                    </Box>
                )}

                {childSuggestions && isBranchTopDown && (
                    <Box>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            Suggested structure per subcategory
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Uncheck a subcategory to leave it unchanged when applying.
                        </Typography>
                        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <ToggleButtonGroup
                                exclusive
                                size="small"
                                value={splitChildrenStrategy}
                                onChange={(_, v) => v && setSplitChildrenStrategy(v)}
                                disabled={loading}
                            >
                                <ToggleButton value="top_down">
                                    <Tooltip
                                        title={getTooltipTextForState(
                                            'structure-categories-strategy-top-down',
                                            true,
                                        )}
                                        placement="top"
                                    >
                                        <span>Top-down</span>
                                    </Tooltip>
                                </ToggleButton>
                                <ToggleButton value="bottom_up">
                                    <Tooltip
                                        title={getTooltipTextForState(
                                            'structure-categories-strategy-bottom-up',
                                            true,
                                        )}
                                        placement="top"
                                    >
                                        <span>Bottom-up</span>
                                    </Tooltip>
                                </ToggleButton>
                            </ToggleButtonGroup>
                            <Box sx={{ minWidth: 160, flex: 1 }}>
                                <Typography variant="body2" gutterBottom>
                                    Depth: {maxDepth}
                                </Typography>
                                <Slider
                                    value={maxDepth}
                                    onChange={(_, value) => setMaxDepth(value as number, true)}
                                    min={1}
                                    max={5}
                                    marks
                                    valueLabelDisplay="auto"
                                    disabled={loading}
                                />
                            </Box>
                            <Button
                                variant="contained"
                                onClick={handleGenerateSplitChildren}
                                disabled={loading}
                            >
                                Retry
                            </Button>
                        </Box>
                        <FormControlLabel
                            control={
                                <Checkbox checked={showContent} onChange={(e) => setShowContent(e.target.checked)} />
                            }
                            label="Show content preview"
                        />
                        {childSuggestions.map((item) => (
                            <Box key={item.categoryId} sx={{ mb: 2 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={acceptedChildIds.has(item.categoryId)}
                                            onChange={() => toggleAcceptedChild(item.categoryId)}
                                            disabled={loading}
                                        />
                                    }
                                    label={
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                            {item.categoryName}
                                        </Typography>
                                    }
                                    sx={{ mb: 1 }}
                                />
                                <Box
                                    sx={{
                                        maxHeight: 280,
                                        overflow: 'auto',
                                        border: 1,
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        p: 2,
                                        opacity: acceptedChildIds.has(item.categoryId) ? 1 : 0.5,
                                        bgcolor: (t) =>
                                            t.palette.mode === 'dark'
                                                ? alpha(t.palette.common.white, 0.04)
                                                : alpha(t.palette.common.black, 0.02),
                                    }}
                                >
                                    {renderTree(item.suggested.tree, '', 0, new Set(), item.suggested.originalContents)}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}

                {suggestedTree && isBranchBottomUp && (
                    <Box>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            Suggested parent layers
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Uncheck a proposed parent group to leave those siblings unchanged.
                        </Typography>
                        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Box sx={{ minWidth: 160, flex: 1 }}>
                                <Typography variant="body2" gutterBottom>
                                    Max height: {maxDepth}
                                </Typography>
                                <Slider
                                    value={maxDepth}
                                    onChange={(_, value) => setMaxDepth(value as number)}
                                    min={1}
                                    max={5}
                                    marks
                                    valueLabelDisplay="auto"
                                    disabled={loading}
                                />
                            </Box>
                            <Button variant="outlined" onClick={handleGenerateGroupSiblings} disabled={loading}>
                                Retry
                            </Button>
                        </Box>
                        {anchor?.parentId &&
                            typeof suggestedTree.tree.name === 'string' &&
                            suggestedTree.tree.name.trim() &&
                            suggestedTree.tree.name.trim() !== anchor.name && (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={applyAiAnchorName}
                                            onChange={(e) => setApplyAiAnchorName(e.target.checked)}
                                        />
                                    }
                                    label={`Also rename “${anchor.name}” to “${suggestedTree.tree.name.trim()}”`}
                                    sx={{ mb: 1 }}
                                />
                            )}
                        <Box
                            sx={{
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                p: 2,
                                maxHeight: 400,
                                overflow: 'auto',
                                bgcolor: (t) =>
                                    t.palette.mode === 'dark'
                                        ? alpha(t.palette.common.white, 0.04)
                                        : alpha(t.palette.common.black, 0.02),
                            }}
                        >
                            {renderGroupSiblingsPreview(suggestedTree.tree, 0, true)}
                        </Box>
                    </Box>
                )}
            </DialogContent>
            
            <DialogActions>
                {suggestedTree && isFromContent && (
                    <>
                        <Button variant="contained" onClick={handleAcceptWithContent} disabled={loading}>
                            Generate with Content
                        </Button>
                        <Button variant="contained" onClick={handleAcceptStructureOnly} disabled={loading}>
                            Generate Structure Only
                        </Button>
                    </>
                )}
                {suggestedTree && isBranchBottomUp && (
                    <Button
                        variant="contained"
                        onClick={handleApplyGroupSiblings}
                        disabled={loading || acceptedParentGroupKeys.size === 0}
                    >
                        Apply
                    </Button>
                )}
                {childSuggestions && isBranchTopDown && (
                    <>
                        <Tooltip title={getTooltipTextForState('subtree-save-with-content', true)} placement="top">
                            <span>
                                <Button
                                    variant="contained"
                                    onClick={handleApplySplitChildrenWithContent}
                                    disabled={loading || acceptedChildIds.size === 0}
                                >
                                    Generate with Content
                                </Button>
                            </span>
                        </Tooltip>
                        <Tooltip title={getTooltipTextForState('subtree-save-structure-only', true)} placement="top">
                            <span>
                                <Button
                                    variant="contained"
                                    onClick={handleApplySplitChildrenStructureOnly}
                                    disabled={loading || acceptedChildIds.size === 0}
                                >
                                    Generate Structure Only
                                </Button>
                            </span>
                        </Tooltip>
                    </>
                )}
                <Button onClick={handleClose}>Cancel</Button>
            </DialogActions>
        </Dialog>
    );
};

export default GenerateSubtreeDialog; 